import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { Button, ButtonWithLoader } from '@neo/index'
import { Card, CardContent } from '@neo/primitives/card'
import type { AxiosError } from 'axios'
import { pick } from 'lodash'
import { flowService } from 'v2/shared/services/flow-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { errorToast } from 'v2/utils/toasts'
import { useCancelCrawl } from '../../queries'
import { KBDocumentSource, type NewKBFormValues } from '../../types'
import { websiteScraperFields } from './website-scraper.config'

interface WebsiteWrapperProps {
  form: GenericFormType<NewKBFormValues>
  onClick?: () => void
}

export const WebsiteWrapper = ({ form, onClick }: WebsiteWrapperProps) => {
  const type = form.useStore((state) => state.values.type)
  const { mutate: cancelCrawl } = useCancelCrawl()

  if (type !== KBDocumentSource.URL) return null

  // Stop an in-progress crawl: cancel the rag-service job and clear the loading state, keeping any
  // pages that already streamed in.
  const handleStop = () => {
    const jobId = form.state.values.jobId
    if (jobId) cancelCrawl(jobId)
    form.setFieldValue('isLoading', false)
  }

  const handleSync = () => {
    form.validateAllFields('change').then((cause) => {
      if (cause.length > 0) return
      // This is for the upload functionality
      if (onClick) {
        onClick()
      }
    })
  }

  const handleCrawl = async (e: React.MouseEvent) => {
    e.preventDefault()
    const validationResults = await Promise.all([
      form.validateField('url', 'change'),
      form.validateField('max_pages', 'change'),
      form.validateField('max_depth', 'change'),
      form.validateField('exclude_paths', 'change'),
      form.validateField('include_paths', 'change'),
    ])

    if (validationResults.some((result) => result.length > 0)) return
    try {
      form.setFieldValue('optionsAccordionOpen', [])
      form.setFieldValue('isLoading', true)
      form.setFieldValue('jobId', '')

      const values = pick(
        form.state.values,
        'url',
        'max_depth',
        'max_pages',
        'crawl_subpages',
        'exclude_paths',
        'include_paths',
      )
      if (!values.url) return
      const response = await flowService.crawlSite(
        values as {
          url: string
          max_depth: number
          max_pages: number
          crawl_subpages: boolean
          exclude_paths: string[]
          include_paths: string[]
        },
      )
      form.setFieldValue('jobId', response.data.job_id)
    } catch (error) {
      errorToast({
        title: 'Failed to crawl site',
        description: `${getErrorText(error as AxiosError)}. Please try again.`,
      })
      form.setFieldValue('isLoading', false)
    }
  }

  const handleDiscard = (e: React.MouseEvent) => {
    e.preventDefault()
    form.setFieldValue('url', '')
    form.setFieldValue('crawl_subpages', form.state.values.url_selection_type === 'selected')
    form.setFieldValue('max_pages', 10)
    form.setFieldValue('max_depth', 1)
    form.setFieldValue('exclude_paths', [])
    form.setFieldValue('include_paths', [])
    form.setFieldValue('links', [])
    form.setFieldValue('jobId', '')
    form.setFieldValue('isLoading', false)
    form.setFieldValue('optionsAccordionOpen', ['options'])
  }

  return (
    <Card variant="noShadow" className="min-h-0 w-full overflow-hidden ">
      <CardContent className="p-4 h-full flex flex-col gap-4 pb-4">
        <FieldsRenderer<NewKBFormValues> form={form} fields={websiteScraperFields} />
        <form.Subscribe
          selector={(state) => [
            state.values.url_selection_type,
            state.values.jobId,
            state.values.isLoading,
            state.values.url,
          ]}
        >
          {([urlSelectionType, jobId, isLoading, url]) => {
            const isCrawled = !!jobId
            // Compute isDisabled based on url and isLoading
            const isDisabled = isLoading || typeof url !== 'string' || url.trim().length === 0

            return (
              <>
                {!isCrawled && (
                  <ButtonWithLoader
                    type={urlSelectionType === 'all' ? 'submit' : 'button'}
                    className="self-end"
                    disabled={isDisabled as boolean}
                    onClick={urlSelectionType === 'all' ? undefined : handleCrawl}
                    loading={!!isLoading}
                  >
                    {urlSelectionType === 'all' ? 'Crawl & sync data' : 'Crawl'}
                  </ButtonWithLoader>
                )}
                {!!isLoading && isCrawled && (
                  <Button
                    variant="outline"
                    className="self-end"
                    type="button"
                    onClick={handleStop}
                    data-testid="button-stop-crawl"
                  >
                    Stop crawl
                  </Button>
                )}
                {isCrawled && urlSelectionType === 'selected' && (
                  <div className="flex justify-between mt-2">
                    <Button variant="outline" data-testid="button-discard" onClick={handleDiscard}>
                      Discard and start over
                    </Button>

                    <Button type="submit" data-testid="button-finish" onClick={handleSync}>
                      Sync data
                    </Button>
                  </div>
                )}
              </>
            )
          }}
        </form.Subscribe>
      </CardContent>
    </Card>
  )
}
